/**
 * AdminEshopTiersTab.jsx
 * ------------------------------------------------------------------
 * Admin UI for editing the four E-shop SEO/GEO tiers (Starter, Basic,
 * Pro, Business) + the eight stackable add-ons. Lets you change price
 * and every numeric/boolean limit without touching code.
 *
 * Each card auto-saves on blur via PATCH /api/admin/eshop-tiers/{id}.
 * Numeric inputs debounce so smash-typing doesn't fire ten requests.
 *
 * Why a single tab instead of one screen per tier? Side-by-side
 * comparison matters more than per-tier detail — the admin's most
 * common job is "Pro is too expensive, lower it but also bump
 * geo_pages_generated to keep the spread justified", and that's
 * easiest done with all four columns visible.
 */
import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { API } from "../../config";
import { Loader2, RefreshCw, CheckCircle, AlertTriangle, Star, Save, Layers, Plus } from "lucide-react";

// Human-readable labels for each limit key — kept here so the admin
// UI doesn't need to know the back-end's exact field names.
const LIMIT_LABELS = {
  eshops:                    "E-shopov",
  products_scanned_monthly:  "Produktov / mes",
  geo_pages_generated:       "GEO stránok",
  cities_in_geo_matrix:      "Miest v matrici",
  ai_rank_checks_daily:      "AI rank checks / deň",
  industries:                "Industries",
  competitors_tracked:       "Konkurenti",
  blog_articles_monthly:     "Blog articles / mes",
  languages:                 "Jazyky",
  white_label:               "White-label",
  custom_domain:             "Vlastná doména",
  api_access:                "API prístup",
};

const BOOL_KEYS = ["white_label", "custom_domain", "api_access"];

const ORDER_TIER_KEYS = [
  "eshops", "products_scanned_monthly", "geo_pages_generated",
  "cities_in_geo_matrix", "ai_rank_checks_daily", "industries",
  "competitors_tracked", "blog_articles_monthly", "languages",
  "white_label", "custom_domain", "api_access",
];

export default function AdminEshopTiersTab() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({}); // package_id -> bool
  const [status, setStatus] = useState({}); // package_id -> 'saved' | 'error'

  // Track dirty edits per package so we can debounce-save just the changed pkg.
  const debounceTimers = useRef({});

  useEffect(() => { loadTiers(); }, []);

  async function loadTiers() {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/eshop-tiers`, {
        headers: { "X-User-Email": getAdminEmail() },
      });
      setTiers(r.data.tiers || []);
    } catch { setTiers([]); }
    setLoading(false);
  }

  function getAdminEmail() {
    try { return localStorage.getItem("user_email") || "admin@toprankpulse.com"; }
    catch { return "admin@toprankpulse.com"; }
  }

  function patchLocal(pkgId, patch) {
    // Optimistic update — patch state immediately so the UI feels
    // instant, then PATCH in the background. If the request fails we
    // re-fetch the full list to recover from the divergence.
    setTiers(prev => prev.map(t => {
      if (t.package_id !== pkgId) return t;
      const merged = { ...t, ...patch };
      if (patch.limits) merged.limits = { ...(t.limits || {}), ...patch.limits };
      if (patch.limits_delta) merged.limits_delta = { ...(t.limits_delta || {}), ...patch.limits_delta };
      return merged;
    }));
    if (debounceTimers.current[pkgId]) clearTimeout(debounceTimers.current[pkgId]);
    debounceTimers.current[pkgId] = setTimeout(() => savePatch(pkgId, patch), 600);
  }

  async function savePatch(pkgId, patch) {
    setSaving(s => ({ ...s, [pkgId]: true }));
    try {
      await axios.patch(`${API}/admin/eshop-tiers/${pkgId}`, patch, {
        headers: { "X-User-Email": getAdminEmail() },
      });
      setStatus(s => ({ ...s, [pkgId]: "saved" }));
      setTimeout(() => setStatus(s => ({ ...s, [pkgId]: null })), 2000);
    } catch (e) {
      setStatus(s => ({ ...s, [pkgId]: "error" }));
      loadTiers(); // recover from divergence
    } finally {
      setSaving(s => ({ ...s, [pkgId]: false }));
    }
  }

  async function reseed() {
    try {
      const r = await axios.post(`${API}/admin/eshop-tiers/reseed`, {}, {
        headers: { "X-User-Email": getAdminEmail() },
      });
      alert(`Reseeded ${r.data.seeded} missing tier(s)/addon(s).`);
      loadTiers();
    } catch (e) { alert("Reseed failed: " + (e.response?.data?.detail || e.message)); }
  }

  // Split tiers vs addons for the two-column layout. Memo because the
  // tier list ~doesn't change after first load, only its values.
  const { mainTiers, addons } = useMemo(() => {
    return {
      mainTiers: tiers.filter(t => t.category === "tier" || t.category === "lifetime"),
      addons:    tiers.filter(t => t.category === "addon"),
    };
  }, [tiers]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="eshop-tiers-loading">
        <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
        <span className="ml-3 text-sm text-gray-400">Načítam tier-y…</span>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-eshop-tiers-tab">
      {/* ----- Header ----- */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black tracking-tight flex items-center gap-2">
            <Layers className="w-6 h-6 text-purple-400" />
            E-shop SEO/GEO · Tiers &amp; Add-ons
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Uprav ceny a limity. Zmeny sa ukladajú automaticky pri opustení poľa.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="eshop-tiers-reseed-btn"
            onClick={reseed}
            className="px-3 py-2 bg-purple-900/30 border border-purple-700/50 hover:bg-purple-900/50 text-sm flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Re-seed missing
          </button>
          <button
            data-testid="eshop-tiers-refresh-btn"
            onClick={loadTiers}
            className="px-3 py-2 bg-gray-900/40 border border-gray-700/60 hover:bg-gray-900/70 text-sm flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* ----- 4 main tiers (grid) ----- */}
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="eshop-tiers-main-grid">
        {mainTiers.map(t => (
          <TierCard
            key={t.package_id}
            tier={t}
            saving={saving[t.package_id]}
            status={status[t.package_id]}
            onChange={patch => patchLocal(t.package_id, patch)}
          />
        ))}
      </div>

      {/* ----- Add-ons (compact list) ----- */}
      <div data-testid="eshop-tiers-addons-list">
        <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
          <Plus className="w-5 h-5 text-amber-400" /> Stackable Add-ons
        </h3>
        <div className="space-y-2">
          {addons.map(a => (
            <AddonRow
              key={a.package_id}
              addon={a}
              saving={saving[a.package_id]}
              status={status[a.package_id]}
              onChange={patch => patchLocal(a.package_id, patch)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// =====================================================================

function TierCard({ tier, saving, status, onChange }) {
  const limits = tier.limits || {};
  return (
    <div
      data-testid={`eshop-tier-card-${tier.package_id}`}
      className={`p-4 border ${tier.popular ? "border-purple-500 ring-1 ring-purple-500/30" : "border-gray-700"} bg-gray-900/40 rounded-sm space-y-3`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <input
            data-testid={`eshop-tier-${tier.package_id}-name`}
            type="text"
            value={tier.name || ""}
            onChange={e => onChange({ name: e.target.value })}
            className="w-full bg-transparent border-b border-gray-800 focus:border-purple-500 font-bold text-base outline-none py-1"
          />
          <input
            type="text"
            value={tier.tagline || ""}
            onChange={e => onChange({ tagline: e.target.value })}
            className="w-full bg-transparent text-xs text-gray-400 outline-none mt-1"
          />
        </div>
        <SaveBadge saving={saving} status={status} />
      </div>

      <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
        <input
          type="checkbox"
          checked={!!tier.popular}
          onChange={e => onChange({ popular: e.target.checked })}
          className="accent-purple-500"
          data-testid={`eshop-tier-${tier.package_id}-popular`}
        />
        <Star className={`w-3 h-3 ${tier.popular ? "text-purple-400 fill-purple-400" : "text-gray-600"}`} />
        Označiť ako "most popular"
      </label>

      <div className="flex items-baseline gap-1 border-t border-gray-800 pt-3">
        <span className="text-gray-500 text-lg">€</span>
        <input
          data-testid={`eshop-tier-${tier.package_id}-price`}
          type="number"
          step="0.01"
          min="0"
          value={tier.price_eur ?? ""}
          onChange={e => onChange({ price_eur: parseFloat(e.target.value) || 0 })}
          className="w-24 bg-transparent text-3xl font-black border-b border-gray-800 focus:border-purple-500 outline-none"
        />
        <span className="text-xs text-gray-500 ml-1">
          / {tier.interval === "one_time" ? "jednorázovo" : tier.interval}
        </span>
      </div>

      <div className="space-y-1 border-t border-gray-800 pt-3">
        {ORDER_TIER_KEYS.map(k => (
          <LimitRow
            key={k}
            limitKey={k}
            value={limits[k]}
            pkgId={tier.package_id}
            isAddon={false}
            onChange={v => onChange({ limits: { [k]: v } })}
          />
        ))}
      </div>
    </div>
  );
}

function AddonRow({ addon, saving, status, onChange }) {
  const delta = addon.limits_delta || {};
  const deltaKeys = Object.keys(delta);
  return (
    <div
      data-testid={`eshop-addon-row-${addon.package_id}`}
      className="flex items-center gap-3 px-4 py-3 bg-gray-900/30 border border-gray-800 rounded-sm"
    >
      <div className="flex-1 min-w-0">
        <input
          type="text"
          value={addon.name || ""}
          onChange={e => onChange({ name: e.target.value })}
          className="w-full bg-transparent font-semibold outline-none text-sm"
        />
        <div className="text-xs text-gray-400 mt-0.5">{addon.tagline}</div>
      </div>

      <div className="flex items-center gap-1">
        <span className="text-gray-500 text-sm">€</span>
        <input
          data-testid={`eshop-addon-${addon.package_id}-price`}
          type="number"
          step="0.01"
          min="0"
          value={addon.price_eur ?? ""}
          onChange={e => onChange({ price_eur: parseFloat(e.target.value) || 0 })}
          className="w-16 bg-transparent text-lg font-bold border-b border-gray-800 focus:border-purple-500 outline-none text-right"
        />
        <span className="text-xs text-gray-500">/{addon.interval === "one_time" ? "ot" : "mo"}</span>
      </div>

      <div className="flex items-center gap-2 min-w-[200px]">
        {deltaKeys.map(k => (
          <LimitRow
            key={k}
            limitKey={k}
            value={delta[k]}
            pkgId={addon.package_id}
            isAddon
            compact
            onChange={v => onChange({ limits_delta: { [k]: v } })}
          />
        ))}
      </div>

      <SaveBadge saving={saving} status={status} />
    </div>
  );
}

function LimitRow({ limitKey, value, pkgId, isAddon, compact, onChange }) {
  const label = LIMIT_LABELS[limitKey] || limitKey;
  const isBool = BOOL_KEYS.includes(limitKey);

  if (compact) {
    // Addon inline: just a number input next to a tiny label.
    if (isBool) {
      return (
        <label className="text-xs flex items-center gap-1">
          <input
            type="checkbox"
            checked={!!value}
            onChange={e => onChange(e.target.checked)}
            className="accent-purple-500"
          />
          {label}
        </label>
      );
    }
    return (
      <div className="flex items-center gap-1 text-xs">
        <span className="text-gray-500">+</span>
        <input
          data-testid={`eshop-addon-${pkgId}-${limitKey}`}
          type="number"
          min="0"
          value={value ?? 0}
          onChange={e => onChange(parseInt(e.target.value, 10) || 0)}
          className="w-14 bg-transparent border-b border-gray-800 focus:border-purple-500 outline-none text-right"
        />
        <span className="text-gray-400">{label}</span>
      </div>
    );
  }

  // Tier card layout — label : value side by side.
  if (isBool) {
    return (
      <label className="flex items-center justify-between text-sm py-1 cursor-pointer hover:bg-gray-900/30 px-2 -mx-2">
        <span className="text-gray-300">{label}</span>
        <input
          data-testid={`eshop-tier-${pkgId}-${limitKey}`}
          type="checkbox"
          checked={!!value}
          onChange={e => onChange(e.target.checked)}
          className="accent-purple-500"
        />
      </label>
    );
  }
  return (
    <div className="flex items-center justify-between text-sm py-1 px-2 -mx-2 hover:bg-gray-900/30">
      <span className="text-gray-300">{label}</span>
      <input
        data-testid={`eshop-tier-${pkgId}-${limitKey}`}
        type="number"
        min="0"
        value={value ?? 0}
        onChange={e => onChange(parseInt(e.target.value, 10) || 0)}
        className="w-20 bg-transparent text-right font-semibold border-b border-gray-800 focus:border-purple-500 outline-none"
      />
    </div>
  );
}

function SaveBadge({ saving, status }) {
  if (saving) return <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />;
  if (status === "saved") return <CheckCircle className="w-4 h-4 text-emerald-400" />;
  if (status === "error") return <AlertTriangle className="w-4 h-4 text-rose-400" />;
  return <Save className="w-4 h-4 text-gray-700" />;
}
